// ... existing imports ...
import { useEffect, useState, useRef, useMemo } from "react";
import Header from "../components/Header";
import { FilterPanel } from "../components/revenue/FilterPanel";
import { BuildingOffice2Icon, MapPinIcon } from '@heroicons/react/24/outline';
import { Search } from 'lucide-react';
import { useData } from "../context/DataContext";
import { useCacheCheck } from "../hooks/useCacheCheck";
import { CacheStatus } from "../components/CacheStatus";
import { ExportButton } from "../components/ExportButton";

// Add TypeScript declaration for window object
declare global {
  interface Window {
    allTeamMembers?: any[];
  }
}

// Add sort configuration type
type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
};

// Add this helper function at the top with other helpers
function formatCurrency(amount: number): string {
  const formattedNumber = amount.toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedNumber}€`;
}

// Define column widths for a more data-focused layout
const clientColumnWidth = 'minmax(120px, 1.2fr)';
const bookedColumnWidth = 'minmax(100px, 1fr)';
const revenueColumnWidth = 'minmax(160px, 2fr)';
const diffColumnWidth = 'minmax(120px, 1fr)';

// --- Main Component ---
export default function AverageRatesPerCity() {
  const [searchTerm, setSearchTerm] = useState('');
  // State for year selectors
  const currentYear = new Date().getFullYear();
  const [yearX, setYearX] = useState<number>(currentYear);
  const [yearY, setYearY] = useState<number>(currentYear - 1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'company', direction: 'asc' });

  const { data, loadingProgress } = useData();
  
  // Check cache when component mounts
  useCacheCheck();

  // Pending filter states (for non-cascading behavior)
  const [pendingCreationYears, setPendingCreationYears] = useState<Set<number>>(new Set());
  const [pendingArrivalYears, setPendingArrivalYears] = useState<Set<number>>(new Set());
  const [pendingManagers, setPendingManagers] = useState<Set<string>>(new Set());
  const [pendingSegments, setPendingSegments] = useState<Set<string>>(new Set());
  const [pendingClients, setPendingClients] = useState<Set<string>>(new Set());
  const [pendingHotels, setPendingHotels] = useState<Set<string>>(new Set());
  const [pendingHotelGroups, setPendingHotelGroups] = useState<Set<string>>(new Set());
  const [pendingMarkets, setPendingMarkets] = useState<Set<string>>(new Set());
  const [pendingArrivalMonths, setPendingArrivalMonths] = useState<Set<number>>(new Set());
  const [pendingArrivalQuarters, setPendingArrivalQuarters] = useState<Set<number>>(new Set());
  const [pendingCreationMonths, setPendingCreationMonths] = useState<Set<number>>(new Set());
  const [pendingCreationQuarters, setPendingCreationQuarters] = useState<Set<number>>(new Set());

  // Applied filter states (what's actually filtering the data)
  const [appliedCreationYears, setAppliedCreationYears] = useState<Set<number>>(new Set());
  const [appliedArrivalYears, setAppliedArrivalYears] = useState<Set<number>>(new Set());
  const [appliedManagers, setAppliedManagers] = useState<Set<string>>(new Set());
  const [appliedSegments, setAppliedSegments] = useState<Set<string>>(new Set());
  const [appliedClients, setAppliedClients] = useState<Set<string>>(new Set());
  const [appliedHotels, setAppliedHotels] = useState<Set<string>>(new Set());
  const [appliedHotelGroups, setAppliedHotelGroups] = useState<Set<string>>(new Set());
  const [appliedMarkets, setAppliedMarkets] = useState<Set<string>>(new Set());
  const [appliedArrivalMonths, setAppliedArrivalMonths] = useState<Set<number>>(new Set());
  const [appliedArrivalQuarters, setAppliedArrivalQuarters] = useState<Set<number>>(new Set());
  const [appliedCreationMonths, setAppliedCreationMonths] = useState<Set<number>>(new Set());
  const [appliedCreationQuarters, setAppliedCreationQuarters] = useState<Set<number>>(new Set());

  // Replace the year state variables
  const [selectedCreationYears, setSelectedCreationYears] = useState<Set<number>>(new Set());
  const [selectedArrivalYears, setSelectedArrivalYears] = useState<Set<number>>(new Set());

  // Add new state for filters
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [clients, setClients] = useState<string[]>([]);

  // Update the state to have separate arrays for each year type
  const [availableCreationYears, setAvailableCreationYears] = useState<number[]>([]);
  const [availableArrivalYears, setAvailableArrivalYears] = useState<number[]>([]);

  // Add new state for managers and segments
  const [selectedManagers, setSelectedManagers] = useState<Set<string>>(new Set());
  const [selectedSegments, setSelectedSegments] = useState<Set<string>>(new Set());
  const [availableManagers, setAvailableManagers] = useState<string[]>([]);
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);

  // Replace single hotel state with multi-select state
  const [selectedHotels, setSelectedHotels] = useState<Set<string>>(new Set());
  const [hotels, setHotels] = useState<string[]>([]);

  // Add new state for arrival months and quarters
  const [selectedArrivalMonths, setSelectedArrivalMonths] = useState<Set<number>>(new Set());
  const [selectedArrivalQuarters, setSelectedArrivalQuarters] = useState<Set<number>>(new Set());

  // Add new state for creation months and quarters
  const [selectedCreationMonths, setSelectedCreationMonths] = useState<Set<number>>(new Set());
  const [selectedCreationQuarters, setSelectedCreationQuarters] = useState<Set<number>>(new Set());

  // Add state for hotel group filter
  const [selectedHotelGroups, setSelectedHotelGroups] = useState<Set<string>>(new Set());
  const [hotelGroups, setHotelGroups] = useState<string[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);

  // Add state for virtual all selection
  const [allClientsSelected, setAllClientsSelected] = useState(true);
  const [allHotelGroupsSelected, setAllHotelGroupsSelected] = useState(true);
  const [allHotelsSelected, setAllHotelsSelected] = useState(true);
  const [allMarketsSelected, setAllMarketsSelected] = useState(true);

  // Update selection handlers for these filters with explicit types (for pending states)
  const handleClientsChange = (newSelected: Set<string>) => {
    setPendingClients(newSelected);
  };
  const handleHotelGroupsChange = (newSelected: Set<string>) => {
    setPendingHotelGroups(newSelected);
  };
  const handleHotelsChange = (newSelected: Set<string>) => {
    setPendingHotels(newSelected);
  };
  const handleMarketsChange = (newSelected: Set<string>) => {
    setPendingMarkets(newSelected);
  };

  // Add initialization ref for tracking when filters have been set up
  const filtersInitialized = useRef(false);

  // After setData(allData); in fetchData or after data is loaded
  useEffect(() => {
    if (!data || data.length === 0 || filtersInitialized.current) return;

    // Years
    const creationYears = Array.from(new Set(data.map(row => row.request_year).filter(Boolean))).sort((a, b) => b - a);
    const arrivalYears = Array.from(new Set(data.map(row => row.arrival_year).filter(Boolean))).sort((a, b) => b - a);

    // Managers
    const managers = Array.from(new Set(data.map(row => row.request_manager).filter(Boolean))).sort();

    // Segments
    const segments = Array.from(new Set(data.map(row => row.segment).filter(Boolean))).sort();

    // Hotels/Venues
    const venues = Array.from(new Set(data.map(row => row.venue).filter(Boolean))).sort();

    // Hotel Groups
    let groups = Array.from(new Set(data.map(row => row.hotel_group_name).filter(Boolean))).sort();
    const hasNoGroup = data.some(row => !row.hotel_group_name);
    if (hasNoGroup) {
      groups = [...groups, 'No Group'];
    }

    // Markets
    let marketValues = Array.from(new Set(data.map(row => row.market).filter(Boolean))).sort();
    const hasNoMarket = data.some(row => !row.market);
    if (hasNoMarket) {
      marketValues = [...marketValues, 'No Market'];
    }

    setHotelGroups(groups);
    setAvailableCreationYears(creationYears);
    setAvailableArrivalYears(arrivalYears);
    
    // Set initial years based on available data
    if (arrivalYears.length > 0) {
      const mostRecentYear = arrivalYears[0]; // Since it's sorted descending
      if (arrivalYears.includes(currentYear)) {
        setYearX(currentYear);
        // Set yearY to the previous year if available, otherwise the most recent year
        const previousYear = currentYear - 1;
        if (arrivalYears.includes(previousYear)) {
          setYearY(previousYear);
        } else {
          setYearY(mostRecentYear);
        }
      } else {
        setYearX(mostRecentYear);
        // Set yearY to the second most recent year if available
        if (arrivalYears.length > 1) {
          setYearY(arrivalYears[1]);
        } else {
          setYearY(mostRecentYear);
        }
      }
    }
    
    setAvailableManagers(managers);
    setAvailableSegments(segments);
    setHotels(venues);
    setMarkets(marketValues);

    const clientsList = Array.from(new Set(data.map((row: any) => row.company).filter(Boolean))).sort();
    setClients(clientsList);

    // Initialize both pending and applied states with all values
    const allManagers = new Set(managers);
    const allSegments = new Set(segments);
    const allCreationYears = new Set(creationYears);
    const allArrivalYears = new Set(arrivalYears);
    const allClients = new Set(clientsList);
    const allHotels = new Set(venues);
    const allHotelGroups = new Set(groups);
    const allMarkets = new Set(marketValues);
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);

    // Set pending state
    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotels);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarkets);
    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);

    // Set applied state
    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotels);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarkets);
    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
    
    // Update "all selected" flags
    setAllClientsSelected(false); // Explicitly selected all
    setAllHotelsSelected(false); // Explicitly selected all
    setAllHotelGroupsSelected(false); // Explicitly selected all
    setAllMarketsSelected(false); // Explicitly selected all

    filtersInitialized.current = true;
  }, [data]);

  // Helper to check if a filter is in 'all' state (empty set or all options selected)
  function isAllSelected<T>(selected: Set<T>, allOptions: T[]) {
    return selected.size === 0 || selected.size === allOptions.length;
  }

  // Manager filter is active if size > 0 (managers don't have "all by default")
  function isManagerFilterActive(selectedManagers: Set<string>, availableManagers: string[]) {
    return selectedManagers.size > 0 && selectedManagers.size !== availableManagers.length;
  }

  // Detect if there are unapplied changes
  const hasUnappliedChanges = useMemo(() => {
    if (!filtersInitialized.current) return false;
    
    const areSetsEqual = <T,>(set1: Set<T>, set2: Set<T>) => {
      if (set1.size !== set2.size) return false;
      for (const item of set1) {
        if (!set2.has(item)) return false;
      }
      return true;
    };
    
    return !areSetsEqual(pendingCreationYears, appliedCreationYears) ||
           !areSetsEqual(pendingArrivalYears, appliedArrivalYears) ||
           !areSetsEqual(pendingManagers, appliedManagers) ||
           !areSetsEqual(pendingSegments, appliedSegments) ||
           !areSetsEqual(pendingClients, appliedClients) ||
           !areSetsEqual(pendingHotels, appliedHotels) ||
           !areSetsEqual(pendingHotelGroups, appliedHotelGroups) ||
           !areSetsEqual(pendingMarkets, appliedMarkets) ||
           !areSetsEqual(pendingArrivalMonths, appliedArrivalMonths) ||
           !areSetsEqual(pendingArrivalQuarters, appliedArrivalQuarters) ||
           !areSetsEqual(pendingCreationMonths, appliedCreationMonths) ||
           !areSetsEqual(pendingCreationQuarters, appliedCreationQuarters);
  }, [pendingCreationYears, appliedCreationYears, pendingArrivalYears, appliedArrivalYears, pendingManagers, appliedManagers, pendingSegments, appliedSegments, pendingClients, appliedClients, pendingHotels, appliedHotels, pendingHotelGroups, appliedHotelGroups, pendingMarkets, appliedMarkets, pendingArrivalMonths, appliedArrivalMonths, pendingArrivalQuarters, appliedArrivalQuarters, pendingCreationMonths, appliedCreationMonths, pendingCreationQuarters, appliedCreationQuarters]);

  // Apply pending filters
  const handleApplyFilters = () => {
    setAppliedCreationYears(new Set(pendingCreationYears));
    setAppliedArrivalYears(new Set(pendingArrivalYears));
    setAppliedManagers(new Set(pendingManagers));
    setAppliedSegments(new Set(pendingSegments));
    setAppliedClients(new Set(pendingClients));
    setAppliedHotels(new Set(pendingHotels));
    setAppliedHotelGroups(new Set(pendingHotelGroups));
    setAppliedMarkets(new Set(pendingMarkets));
    setAppliedArrivalMonths(new Set(pendingArrivalMonths));
    setAppliedArrivalQuarters(new Set(pendingArrivalQuarters));
    setAppliedCreationMonths(new Set(pendingCreationMonths));
    setAppliedCreationQuarters(new Set(pendingCreationQuarters));
  };

  // Compute possible managers based on all other filters (cascading logic)

  // Repeat this pattern for all other possible... sets:
  // possibleHotelGroups, possibleHotels, possibleClients, possibleCreationYears, possibleCreationMonths, possibleCreationQuarters, possibleArrivalYears, possibleArrivalMonths, possibleArrivalQuarters

  // Date filters

  // Prune date filter sets when possible options change, but only update if changed and only if selection is non-empty and possible set is non-empty

  // Prune and auto-select possible values for Clients, Hotel Groups, and Hotels

  // Restore resetFilters function
  const resetFilters = () => {
    // Reset all filters to all values
    const allManagers = new Set(availableManagers);
    const allSegments = new Set(availableSegments);
    const allCreationYears = new Set(availableCreationYears);
    const allArrivalYears = new Set(availableArrivalYears);
    const allClients = new Set(clients);
    const allHotels = new Set(hotels);
    const allHotelGroups = new Set(hotelGroups);
    const allMarkets = new Set(markets);
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);
    
    // Reset pending state
    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);
    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotels);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarkets);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);
    
    // Also reset applied state (so data refreshes immediately)
    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);
    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotels);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarkets);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);
    
    // Update "all selected" flags
    setAllClientsSelected(false);
    setAllHotelsSelected(false);
    setAllHotelGroupsSelected(false);
    setAllMarketsSelected(false);
  };

  // Filtered data using the search term and all other filters (use applied* states)
  const filteredData = useMemo(() => {
    return data.filter(row => {
      // Search filter
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const matchesSearch = 
          (row.destination_city && row.destination_city.toLowerCase().includes(lower)) ||
          (row.company && row.company.toLowerCase().includes(lower)) ||
          (row.request_manager && row.request_manager.toLowerCase().includes(lower)) ||
          (row.segment && row.segment.toLowerCase().includes(lower));
        if (!matchesSearch) return false;
      }

      // FilterPanel filters (use applied state)
      if (isManagerFilterActive(appliedManagers, availableManagers) && !appliedManagers.has(row.request_manager)) return false;
      if (!isAllSelected(appliedClients, clients) && !appliedClients.has(row.company)) return false;
      if (!isAllSelected(appliedArrivalYears, availableArrivalYears) && !appliedArrivalYears.has(row.arrival_year)) return false;
      if (!isAllSelected(appliedCreationYears, availableCreationYears) && !appliedCreationYears.has(row.request_year)) return false;
      if (!isAllSelected(appliedSegments, availableSegments) && !appliedSegments.has(row.segment)) return false;
      const groupName = row.hotel_group_name ? row.hotel_group_name : 'No Group';
      if (!isAllSelected(appliedHotelGroups, hotelGroups) && !appliedHotelGroups.has(groupName)) return false;
      if (!isAllSelected(appliedHotels, hotels) && !appliedHotels.has(row.venue)) return false;
      if (!isAllSelected(appliedArrivalMonths, [1,2,3,4,5,6,7,8,9,10,11,12]) && !appliedArrivalMonths.has(row.arrival_month)) return false;
      if (!isAllSelected(appliedArrivalQuarters, [1,2,3,4]) && !appliedArrivalQuarters.has(row.arrival_quarter)) return false;
      if (!isAllSelected(appliedCreationMonths, [1,2,3,4,5,6,7,8,9,10,11,12]) && !appliedCreationMonths.has(row.request_month)) return false;
      if (!isAllSelected(appliedCreationQuarters, [1,2,3,4]) && !appliedCreationQuarters.has(row.request_quarter)) return false;
      const marketName = row.market ? row.market : 'No Market';
      if (!isAllSelected(appliedMarkets, markets) && !appliedMarkets.has(marketName)) return false;

      return true;
    });
  }, [data, searchTerm, appliedManagers, availableManagers, appliedClients, clients, appliedArrivalYears, availableArrivalYears, appliedCreationYears, availableCreationYears, appliedSegments, availableSegments, appliedHotelGroups, hotelGroups, appliedHotels, hotels, appliedArrivalMonths, appliedArrivalQuarters, appliedCreationMonths, appliedCreationQuarters, appliedMarkets, markets]);

  // Group data by segment, then city
  const groupedData = useMemo(() => {
    // Structure: { [segment]: { [city]: [rows] } }
    const result: Record<string, Record<string, any[]>> = {};
    filteredData.forEach(row => {
      if (!result[row.segment]) result[row.segment] = {};
      if (!result[row.segment][row.destination_city || 'Unknown City']) {
        result[row.segment][row.destination_city || 'Unknown City'] = [];
      }
      result[row.segment][row.destination_city || 'Unknown City'].push(row);
    });
    return result;
  }, [filteredData]);

  // --- Render helpers ---
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [expandedCities, setExpandedCities] = useState<Set<string>>(new Set());
  
  // Handle sort click
  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort data function
  const sortData = (data: any[]) => {
    return [...data].sort((a, b) => {
      let aValue: any = a[sortConfig.key];
      let bValue: any = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle strings
      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();
      return sortConfig.direction === 'asc' 
        ? aString.localeCompare(bString) 
        : bString.localeCompare(aString);
    });
  };

  // Update the grid style calculation
  const gridStyle = useMemo(() => {
    return {
      display: 'grid',
      gridTemplateColumns: [
        clientColumnWidth,
        bookedColumnWidth,
        bookedColumnWidth,
        revenueColumnWidth,
        revenueColumnWidth,
        revenueColumnWidth,
        revenueColumnWidth,
        diffColumnWidth,
        diffColumnWidth
      ].join(' '),
      alignItems: 'center'
    };
  }, []);

  // Create flat data structure for sorting and total calculation
  const flatData = useMemo(() => {
    const data: any[] = [];
    Object.entries(groupedData).forEach(([segment, cities]) => {
      const allSegmentRows = Object.values(cities as any).flat() as any[];
      
      // Filter for Booked requests only
      const bookedRowsX = allSegmentRows.filter((r: any) => r.status === 'Booked' && r.arrival_year === yearX && r.price);
      const bookedRowsY = allSegmentRows.filter((r: any) => r.status === 'Booked' && r.arrival_year === yearY && r.price);
      
      const bookedX = bookedRowsX.length;
      const bookedY = bookedRowsY.length;
      
      // Calculate average prices
      const avgPriceX = bookedRowsX.length > 0 
        ? bookedRowsX.reduce((sum: number, r: any) => sum + (r.price || 0), 0) / bookedRowsX.length 
        : 0;
      const avgPriceY = bookedRowsY.length > 0 
        ? bookedRowsY.reduce((sum: number, r: any) => sum + (r.price || 0), 0) / bookedRowsY.length 
        : 0;
      
      const diffPrice = avgPriceX - avgPriceY;
      const diffPricePercent = avgPriceY !== 0 ? ((diffPrice / avgPriceY) * 100) : null;
      
      if (bookedX === 0 && bookedY === 0) return;
      
      data.push({
        segment,
        cities,
        bookedX,
        bookedY,
        avgPriceX,
        avgPriceY,
        diffPrice,
        diffPricePercent
      });
    });
    // Sort segments alphabetically by default
    data.sort((a, b) => a.segment.localeCompare(b.segment));
    return data;
  }, [groupedData, yearX, yearY]);

  // Apply sorting
  const sortedData = useMemo(() => sortData(flatData), [flatData, sortConfig]);

  // Calculate totals with correct weighted averages
  const totals = useMemo(() => {
    let totalBookedX = 0;
    let totalBookedY = 0;
    let totalRevenueX = 0;
    let totalRevenueY = 0;
    
    sortedData.forEach(row => {
      totalBookedX += (row.bookedX || 0);
      totalBookedY += (row.bookedY || 0);
      // Calculate total revenue for each year (average price × number of bookings)
      totalRevenueX += (row.avgPriceX || 0) * (row.bookedX || 0);
      totalRevenueY += (row.avgPriceY || 0) * (row.bookedY || 0);
    });
    
    const trueAvgPriceX = totalBookedX > 0 ? totalRevenueX / totalBookedX : 0;
    const trueAvgPriceY = totalBookedY > 0 ? totalRevenueY / totalBookedY : 0;
    
    return {
      bookedX: totalBookedX,
      bookedY: totalBookedY,
      avgPriceX: trueAvgPriceX,
      avgPriceY: trueAvgPriceY,
      diffPrice: trueAvgPriceX - trueAvgPriceY
    };
  }, [sortedData]);

  // Table data transformer for export (level 1 only - segments)
  const getTableDataForExport = () => {
    const tableData = sortedData.map(({ segment, bookedX, bookedY, avgPriceX, avgPriceY, diffPrice, diffPricePercent }) => ({
      Segment: segment,
      [`Groups ${yearX}`]: bookedX,
      [`Groups ${yearY}`]: bookedY,
      [`Average Price ${yearX}`]: avgPriceX,
      [`Average Price ${yearY}`]: avgPriceY,
      'Price Diff': diffPrice,
      'Price Diff %': diffPricePercent !== null ? diffPricePercent : 0
    }));

    // Add TOTAL row if there are 2 or more rows
    if (sortedData.length >= 2) {
      const totalDiffPricePercent = totals.avgPriceY !== 0 ? ((totals.diffPrice / totals.avgPriceY) * 100) : 0;

      tableData.push({
        Segment: 'TOTAL',
        [`Groups ${yearX}`]: totals.bookedX,
        [`Groups ${yearY}`]: totals.bookedY,
        [`Average Price ${yearX}`]: totals.avgPriceX,
        [`Average Price ${yearY}`]: totals.avgPriceY,
        'Price Diff': totals.diffPrice,
        'Price Diff %': totalDiffPricePercent
      });
    }

    return tableData;
  };

  function renderRows() {
    const rows: JSX.Element[] = [];
    
    sortedData.forEach(({ segment, cities, bookedX, bookedY, avgPriceX, avgPriceY, diffPrice, diffPricePercent }) => {
      if (bookedX === 0 && bookedY === 0) return;
      const isSegmentExpanded = expandedCompanies.has(segment);
      rows.push(
        <tr key={segment} className="bg-gray-50 hover:bg-gray-100 hover:cursor-pointer" onClick={() => {
          setExpandedCompanies(prev => {
            const next = new Set(prev);
            if (next.has(segment)) next.delete(segment); else next.add(segment);
            return next;
          });
        }}>
          <td className="px-2 py-2 whitespace-nowrap text-sm font-semibold text-gray-900 min-w-[200px]">{segment}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{bookedX}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{bookedY}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(avgPriceX)}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(avgPriceY)}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs">
            <div className="flex flex-row items-center gap-2 justify-end">
              <span>{formatCurrency(diffPrice)}</span>
              {diffPricePercent !== null ? (
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${diffPricePercent > 0 ? 'bg-green-100 text-green-800' : diffPricePercent < 0 ? 'bg-red-100 text-red-800' : ''}`}
                  style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                  {Math.round(diffPricePercent)}%
                </span>
              ) : (
                <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-center bg-gray-100 text-gray-400"
                  style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                  -
                </span>
              )}
            </div>
          </td>
        </tr>
      );
      if (isSegmentExpanded) {
        // Sort cities alphabetically
        const sortedCities = Object.entries(cities as any).map(([city, cityRows]) => {
          const bookedCityRowsX = (cityRows as any[]).filter((r: any) => r.status === 'Booked' && r.arrival_year === yearX && r.price);
          const bookedCityRowsY = (cityRows as any[]).filter((r: any) => r.status === 'Booked' && r.arrival_year === yearY && r.price);
          
          const avgCityPriceX = bookedCityRowsX.length > 0 
            ? bookedCityRowsX.reduce((sum: number, r: any) => sum + (r.price || 0), 0) / bookedCityRowsX.length 
            : 0;
          const avgCityPriceY = bookedCityRowsY.length > 0 
            ? bookedCityRowsY.reduce((sum: number, r: any) => sum + (r.price || 0), 0) / bookedCityRowsY.length 
            : 0;
          
          return { city, cityRows, avgCityPriceX, avgCityPriceY };
        }).sort((a, b) => a.city.localeCompare(b.city));

        sortedCities.forEach(({ city, cityRows, avgCityPriceX, avgCityPriceY }) => {
          const bookedX = (cityRows as any[]).filter((r: any) => r.status === 'Booked' && r.arrival_year === yearX && r.price).length;
          const bookedY = (cityRows as any[]).filter((r: any) => r.status === 'Booked' && r.arrival_year === yearY && r.price).length;
          
          if (bookedX === 0 && bookedY === 0) return;
          
          const cityKey = `${segment}||${city}`;
          const isCityExpanded = expandedCities.has(cityKey);
          
          rows.push(
            <tr key={cityKey} className="bg-gray-50 border-l-4 border-blue-200 hover:bg-gray-100 hover:cursor-pointer" onClick={() => {
              setExpandedCities(prev => {
                const next = new Set(prev);
                if (next.has(cityKey)) next.delete(cityKey); else next.add(cityKey);
                return next;
              });
            }}>
              <td className="pl-8 pr-2 py-2 whitespace-nowrap text-xs text-gray-700 min-w-[200px] font-normal">
                <span className="flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-1 text-blue-400 flex-shrink-0" />
                  {city}
                </span>
              </td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{bookedX}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{bookedY}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(avgCityPriceX)}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(avgCityPriceY)}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs">
                <div className="flex flex-row items-center gap-2 justify-end">
                  <span>{formatCurrency(avgCityPriceX - avgCityPriceY)}</span>
                  {avgCityPriceY !== 0 ? (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${(avgCityPriceX - avgCityPriceY) > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                      style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                      {Math.round(((avgCityPriceX - avgCityPriceY) / avgCityPriceY) * 100)}%
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-center bg-gray-100 text-gray-400"
                      style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                      -
                    </span>
                  )}
                </div>
              </td>
            </tr>
          );
          
          // If city is expanded, show hotels
          if (isCityExpanded) {
            // Group hotels within this city
            const hotelsByName: Record<string, any[]> = {};
            (cityRows as any[]).forEach((row: any) => {
              const hotelName = row.venue || 'Unknown Hotel';
              if (!hotelsByName[hotelName]) hotelsByName[hotelName] = [];
              hotelsByName[hotelName].push(row);
            });
            
            // Sort hotels alphabetically
            const sortedHotels = Object.entries(hotelsByName).sort((a, b) => a[0].localeCompare(b[0]));
            
            sortedHotels.forEach(([hotel, hotelRows]) => {
              const bookedHotelRowsX = hotelRows.filter((r: any) => r.status === 'Booked' && r.arrival_year === yearX && r.price);
              const bookedHotelRowsY = hotelRows.filter((r: any) => r.status === 'Booked' && r.arrival_year === yearY && r.price);
              
              const hotelBookedX = bookedHotelRowsX.length;
              const hotelBookedY = bookedHotelRowsY.length;
              
              if (hotelBookedX === 0 && hotelBookedY === 0) return;
              
              const avgHotelPriceX = bookedHotelRowsX.length > 0 
                ? bookedHotelRowsX.reduce((sum: number, r: any) => sum + (r.price || 0), 0) / bookedHotelRowsX.length 
                : 0;
              const avgHotelPriceY = bookedHotelRowsY.length > 0 
                ? bookedHotelRowsY.reduce((sum: number, r: any) => sum + (r.price || 0), 0) / bookedHotelRowsY.length 
                : 0;
              
              const hotelKey = `${cityKey}||${hotel}`;
              
              rows.push(
                <tr key={hotelKey} className="bg-white border-l-8 border-blue-100">
                  <td className="pl-16 pr-2 py-2 whitespace-nowrap text-xs text-gray-600 min-w-[200px] font-normal">
                    <span className="flex items-center">
                      <BuildingOffice2Icon className="w-4 h-4 mr-1 text-blue-300 flex-shrink-0" />
                      {hotel}
                    </span>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{hotelBookedX}</td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{hotelBookedY}</td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(avgHotelPriceX)}</td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(avgHotelPriceY)}</td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    <div className="flex flex-row items-center gap-2 justify-end">
                      <span>{formatCurrency(avgHotelPriceX - avgHotelPriceY)}</span>
                      {avgHotelPriceY !== 0 ? (
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${(avgHotelPriceX - avgHotelPriceY) > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                          style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                          {Math.round(((avgHotelPriceX - avgHotelPriceY) / avgHotelPriceY) * 100)}%
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-center bg-gray-100 text-gray-400"
                          style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                          -
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            });
          }
        });
      }
    });
    return rows;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="page-title">Average Rates per Segment / City</h1>
          <div className="flex items-center space-x-4">
            <ExportButton
              reportName="Average Rates per Segment City"
              data={data}
              filters={{
                selectedCreationYears: appliedCreationYears,
                selectedArrivalYears: appliedArrivalYears,
                selectedManagers: appliedManagers,
                selectedSegments: appliedSegments,
                selectedClients: appliedClients,
                selectedHotels: appliedHotels,
                selectedHotelGroups: appliedHotelGroups,
                selectedArrivalMonths: appliedArrivalMonths,
                selectedArrivalQuarters: appliedArrivalQuarters,
                selectedCreationMonths: appliedCreationMonths,
                selectedCreationQuarters: appliedCreationQuarters,
                allClientsSelected,
                allHotelsSelected,
                allHotelGroupsSelected
              }}
              tableDataTransformer={getTableDataForExport}
            />
            <CacheStatus />
          </div>
        </div>
        <div className="mb-6">
          <FilterPanel
            selectedCreationYears={pendingCreationYears}
            selectedArrivalYears={pendingArrivalYears}
            selectedManagers={pendingManagers}
            selectedSegments={pendingSegments}
            selectedClients={pendingClients}
            selectedHotels={pendingHotels}
            selectedHotelGroups={pendingHotelGroups}
            selectedMarkets={pendingMarkets}
            availableCreationYears={availableCreationYears}
            availableArrivalYears={availableArrivalYears}
            availableManagers={availableManagers}
            availableSegments={availableSegments}
            clients={clients}
            hotels={hotels}
            hotelGroups={hotelGroups}
            markets={markets}
            setSelectedCreationYears={setPendingCreationYears}
            setSelectedArrivalYears={setPendingArrivalYears}
            setSelectedManagers={setPendingManagers}
            setSelectedSegments={setPendingSegments}
            resetFilters={resetFilters}
            selectedArrivalMonths={pendingArrivalMonths}
            selectedArrivalQuarters={pendingArrivalQuarters}
            setSelectedArrivalMonths={setPendingArrivalMonths}
            setSelectedArrivalQuarters={setPendingArrivalQuarters}
            selectedCreationMonths={pendingCreationMonths}
            selectedCreationQuarters={pendingCreationQuarters}
            setSelectedCreationMonths={setPendingCreationMonths}
            setSelectedCreationQuarters={setPendingCreationQuarters}
            onClientsChange={handleClientsChange}
            allClientsSelected={allClientsSelected}
            onHotelsChange={handleHotelsChange}
            allHotelsSelected={allHotelsSelected}
            onHotelGroupsChange={handleHotelGroupsChange}
            allHotelGroupsSelected={allHotelGroupsSelected}
            onMarketsChange={handleMarketsChange}
            allMarketsSelected={allMarketsSelected}
            onApplyFilters={handleApplyFilters}
            hasUnappliedChanges={hasUnappliedChanges}
          />
        </div>
        {loadingProgress && (
          <div className="mb-4 bg-blue-50 text-blue-700 p-2 rounded-md text-center">
            {loadingProgress}
          </div>
        )}
        <div className="flex gap-4 mb-4 items-end justify-between">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by destination city..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700">Arrival Year X</label>
              <select value={yearX} onChange={e => setYearX(Number(e.target.value))} className="border rounded px-2 py-1">
                {availableArrivalYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700">Arrival Year Y</label>
              <select value={yearY} onChange={e => setYearY(Number(e.target.value))} className="border rounded px-2 py-1">
                {availableArrivalYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: clientColumnWidth }} />
                <col style={{ width: bookedColumnWidth }} />
                <col style={{ width: bookedColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: diffColumnWidth }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('segment')}
                  >
                    <div className="flex items-center gap-1">
                      SEGMENT
                      {sortConfig?.key === 'segment' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('bookedX')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      GROUPS {yearX}
                      {sortConfig?.key === 'bookedX' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('bookedY')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      GROUPS {yearY}
                      {sortConfig?.key === 'bookedY' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('avgPriceX')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      AVERAGE PRICE {yearX}
                      {sortConfig?.key === 'avgPriceX' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('avgPriceY')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      AVERAGE PRICE {yearY}
                      {sortConfig?.key === 'avgPriceY' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('diffPrice')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      PRICE DIFF
                      {sortConfig?.key === 'diffPrice' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {renderRows()}
                {/* Add the total row if there are 2 or more visible rows */}
                {sortedData.length >= 2 && (
                  <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold">
                    <td className="px-2 py-1.5 text-sm text-gray-700">TOTAL</td>
                    <td className="px-2 py-1.5 text-xs text-center">{totals.bookedX}</td>
                    <td className="px-2 py-1.5 text-xs text-center">{totals.bookedY}</td>
                    <td className="px-2 py-1.5 text-xs text-center">{formatCurrency(totals.avgPriceX)}</td>
                    <td className="px-2 py-1.5 text-xs text-center">{formatCurrency(totals.avgPriceY)}</td>
                    <td className="px-2 py-1.5 text-xs text-center">
                      <div className="flex flex-row items-center gap-2 justify-end">
                        <span>{formatCurrency(totals.diffPrice)}</span>
                        {totals.avgPriceY > 0 && (
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${
                            totals.diffPrice > 0 ? 'bg-green-100 text-green-800' : totals.diffPrice < 0 ? 'bg-red-100 text-red-800' : ''
                          }`}
                            style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                            {Math.round((totals.diffPrice / totals.avgPriceY) * 100)}%
                          </span>
                        )}
                      </div>
                    </td>

                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}